/**
 * Support access: the platform's support inside a restaurant's back office,
 * with the owner's consent.
 *
 * Three sides, one file:
 *
 * - **The owner** (`identity.support_access.grant`): see the grants and the
 *   visits under them, give one — for how long, read-only unless changes are
 *   ticked, with a note — and end one at once.
 * - **The back office opening a support link** (`useOpenSupportLink`): the
 *   one-time code from the link, exchanged once for the session's token, which
 *   is stored under the back office's *support* storage key, never the staff
 *   one — a manager's own session on the same device is never touched.
 * - **The support session** (`useSupportBanner`, `useEndSupportSession`):
 *   what the permanent banner says, and ending the session from it.
 *
 * The server enforces all of it on every request: a read-only session's writes
 * answer 403 `support_read_only`, what support may never do answers 403
 * `support_not_allowed`, and a session whose grant has ended answers 401,
 * which ends it here. The screens only say so.
 *
 * The operator's side lives in `platform-support.ts`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError, SessionTokens } from './client';
import { useApi, useSession, useStartSession } from './provider';

export interface SupportVisit {
  id: string;
  operator_name: string;
  read_only: boolean;
  started_at: string;
  expires_at: string;
  ended_at: string | null;
  /** `operator` or `owner` when ended early; null when it ran out or runs on. */
  ended_how: 'operator' | 'owner' | null;
  /** Whether the link was opened at all. */
  opened: boolean;
}

export interface SupportGrant {
  id: string;
  granted_by: string;
  granted_by_name: string;
  granted_at: string;
  expires_at: string;
  allow_changes: boolean;
  note: string;
  ended_at: string | null;
  ended_by_name: string | null;
  /** Neither ended nor run out: support can open a session now. */
  live: boolean;
  sessions: SupportVisit[];
}

export interface SupportAccess {
  current: SupportGrant | null;
  /** Newest first, the live one included. */
  grants: SupportGrant[];
  /** The longest a grant may be, in hours. */
  max_hours: number;
}

export interface GrantSupportInput {
  hours: number;
  /** Off unless ticked: support looks, and changes nothing. */
  allow_changes: boolean;
  note: string;
}

export function useSupportAccess(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['support-access'],
    queryFn: ({ signal }) => api.get<SupportAccess>('/v1/support-access', signal),
    enabled,
  });
}

export function useGrantSupportAccess() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<SupportGrant, ApiError, GrantSupportInput>({
    mutationFn: (input) => api.post<SupportGrant>('/v1/support-access', input),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['support-access'] }),
  });
}

/** End it now. A session under it is refused from its next click. */
export function useEndSupportAccess() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<SupportGrant, ApiError, string>({
    mutationFn: (grantId) => api.post<SupportGrant>(`/v1/support-access/${grantId}/end`),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['support-access'] }),
  });
}

// --------------------------------------------------------------------------- //
// The support session, in the back office
// --------------------------------------------------------------------------- //

export interface SupportToken {
  access_token: string;
  token_type: string;
  /** Seconds. No refresh token: when it runs out, the session is over. */
  expires_in: number;
  tenant_id: string;
  session_id: string;
}

export interface SupportBanner {
  session_id: string;
  restaurant_name: string;
  operator_name: string;
  read_only: boolean;
  /** When the session stops on its own. */
  ends_at: string;
}

/** A support session as the client stores it: no refresh token behind it. */
export function supportSession(token: SupportToken, now = Date.now()): SessionTokens {
  return {
    access: token.access_token,
    refresh: null,
    expiresAt: now + token.expires_in * 1000,
  };
}

/**
 * Exchange the code from a support link for the session, once. A 401 is an
 * answer about the link (used, or run out), so it ends nothing.
 */
export function useOpenSupportLink() {
  const api = useApi();
  const start = useStartSession();
  return useMutation<SupportToken, ApiError, string>({
    mutationFn: (code) =>
      api.post<SupportToken>(
        '/v1/auth/support',
        { code },
        { unauthenticatedEndsSession: false },
      ),
    onSuccess: (token) => start(supportSession(token)),
  });
}

export function useSupportBanner() {
  const api = useApi();
  const session = useSession();
  return useQuery({
    queryKey: ['support-session', session.epoch],
    queryFn: ({ signal }) => api.get<SupportBanner>('/v1/support-session', signal),
    enabled: session.isAuthenticated,
    // Every minute: a session the owner ended shows as ended without a click.
    refetchInterval: 60_000,
  });
}

/** End the session now. Its token is refused from the next request. */
export function useEndSupportSession() {
  const api = useApi();
  const session = useSession();
  return useMutation<void, ApiError, void>({
    mutationFn: () => api.post<void>('/v1/support-session/end'),
    onSettled: () => session.signOut(),
  });
}
